import { Loader2 } from "lucide-react";
import { ImageFilter } from "../models/Post";
import { ImageFilterPicker } from "./ImageFilterPicker";
import { ImageSourceButton } from "./ImageSourceButton";
import { useAuth } from "../contexts/AuthContext";
import { Label } from "./ui/label";
import type {
  BrandColorShortcut,
  BrandKitColorPickerContext,
} from "./BrandColorShortcuts";

const ADMIN_EMAILS = [
  "lu@wavegen.ai",
  "lulu.li1989@gmail.com",
  "experisy@gmail.com",
  "lululihq@gmail.com",
];

interface BackgroundFormatControlsProps {
  previewUrl: string | null;
  hasImage: boolean;
  loading?: boolean;
  initialBackgroundColor?: string | null;
  onUpload: () => void;
  onBrowseLibrary: () => void;
  onChangeColor?: (color: string) => void;
  filter: ImageFilter | null | undefined;
  onFilterChange?: (filter: ImageFilter | undefined) => void;
  isTemplateAsset?: boolean;
  onTemplateAssetChange?: (next: boolean) => void;
  brandColors?: BrandColorShortcut[];
  brandKitContext?: BrandKitColorPickerContext;
}

export function BackgroundFormatControls({
  previewUrl,
  hasImage,
  loading,
  initialBackgroundColor,
  onUpload,
  onBrowseLibrary,
  onChangeColor,
  filter,
  onFilterChange,
  isTemplateAsset,
  onTemplateAssetChange,
  brandColors,
  brandKitContext,
}: BackgroundFormatControlsProps) {
  const { currentUser } = useAuth();
  const isAdmin = ADMIN_EMAILS.includes(currentUser?.email || "");

  return (
    <div className="space-y-6">
      <div className="space-y-2 pb-4 border-b">
        <Label>Image Source</Label>
        {loading ? (
          <div className="w-full h-32 rounded-md bg-muted flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : previewUrl ? (
          <div className="w-full h-32 rounded-md bg-slate-100 flex items-center justify-center overflow-hidden">
            <img
              src={previewUrl}
              alt="Background preview"
              className="h-full w-full object-contain"
            />
          </div>
        ) : null}
        <ImageSourceButton
          label={hasImage ? "Change" : "Upload"}
          onUpload={onUpload}
          onBrowseLibrary={onBrowseLibrary}
          loading={loading}
          align="start"
          onChangeColor={onChangeColor}
          currentColor={initialBackgroundColor || "#000000"}
          brandColors={brandColors}
          brandKitContext={brandKitContext}
        />
      </div>

      {onFilterChange && hasImage && (
        <ImageFilterPicker
          previewUrl={previewUrl}
          value={filter}
          onChange={onFilterChange}
        />
      )}

      {isAdmin && onTemplateAssetChange && (
        <div className="space-y-2 pt-4 border-t">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isTemplateAsset || false}
              onChange={(e) => onTemplateAssetChange(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm">Template asset (static)</span>
          </label>
        </div>
      )}
    </div>
  );
}
